import { AppInfo } from '@capacitor/app';
import { DeviceInfo } from '@capacitor/device';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { withSessionStorage, withStorageSync } from '@ngrx-toolkit/core';

import { ScanType } from '@/app/app/constants/scan-type.enum';
import { ActionOtdr } from '@/app/app/interfaces/action-page/action-otdr';
import { ActionSettings } from '@/app/app/interfaces/action-page/action-settings';
import { ActionThreshold } from '@/app/app/interfaces/action-page/action-threshold';
import { ReportDetails } from '@/app/app/interfaces/scan/report-details';
import { storagePrefix } from '@/shared/lib/constants/storage.const';

// Visible graph range, remembered while "Fix graph position" is on (session only: it changes on every zoom/pan)
export type GraphViewRange = { xStart: number; xEnd: number; yStart?: number; yEnd?: number };

type DataState = {
  reportList: ReportDetails[],
  appInfo: AppInfo | null,
  deviceInfo: DeviceInfo | null,
  deviceId: string | null,
  pushToken: string | null,
  pushTokenRegistered: boolean,
  graphView: GraphViewRange | null,
};

const initialState: DataState = {
  reportList: [],
  appInfo: null,
  deviceInfo: null,
  deviceId: null,
  pushToken: null,
  pushTokenRegistered: false,
  graphView: null,
};

export const DataStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => ({
    setReport(report: ReportDetails) {
      const currentList = store.reportList();
      const currentReport = currentList.findIndex(f => f.scanType === report.scanType);

      if (currentReport !== -1) {
        currentList[currentReport] = report;
      } else {
        currentList.push(report);
      }

      patchState(store, { reportList: currentList });
    },
    removeReport(scanType: number) {
      const currentList = store.reportList();
      const currentReport = currentList.findIndex(f => f.scanType === scanType);

      // remove currentReport from array
      if (currentReport !== -1) {
        currentList.splice(currentReport, 1);
      }
      patchState(store, { reportList: currentList });
    },
    getReport(scanType: number) {
      const currentList = store.reportList();
      return currentList.find(f => f.scanType === scanType);
    },
    clear() {
      patchState(store, { reportList: [] });
    },
    setAppInfo(appInfo: AppInfo) {
      patchState(store, { appInfo });
    },
    setDeviceInfo(deviceInfo: DeviceInfo) {
      patchState(store, { deviceInfo });
    },
    setDeviceId(deviceId: string) {
      patchState(store, { deviceId });
    },
    setGraphView(graphView: GraphViewRange | null) {
      patchState(store, { graphView });
    },
    setPushToken(pushToken: string) {
      patchState(store, { pushToken, pushTokenRegistered: false });
    },
    setPushTokenRegistered(pushTokenRegistered: boolean) {
      patchState(store, { pushTokenRegistered });
    },
    getActionFeatures(scanType: number) {
      let actionSettings: ActionSettings;
      let actionThresholds: ActionThreshold[];
      let actionOtdr: ActionOtdr;

      if (scanType === ScanType.Scan) {
        actionSettings = {
          Range: true,
          AverageTime: true,
          WaveLength: true,
          Ior: true,
          Pulse: true,
        };

        actionOtdr = {
          SamplingRate: true,
          MeasurementMode: true,
          FiberEndDetection: true,
          LossDetection: true,
          ReflectanceDetection: true,
          LaunchFiberStart: false,
          LaunchFiberEnd: false,
          Wavelength: true,
        };
        actionThresholds = [];
      } else if (scanType === ScanType.Realtime) {
        actionSettings = {
          Range: true,
          AverageTime: true,
          WaveLength: true,
          Ior: true,
          Pulse: true,
        };

        actionOtdr = {
          SamplingRate: true,
          MeasurementMode: true,
          FiberEndDetection: true,
          LossDetection: true,
          ReflectanceDetection: true,
          LaunchFiberStart: false,
          LaunchFiberEnd: false,
          Wavelength: true,
        };
        actionThresholds = [];
      } else if (scanType === ScanType.VisualFaultLocator) {
        actionSettings = {
          Range: false,
          AverageTime: true,
          WaveLength: false,
          Ior: false,
          Pulse: false,
        };

        actionOtdr = new ActionOtdr();
        actionThresholds = [];
      } else if (scanType === ScanType.MonitoringStart) {
        actionSettings = {
          Range: true,
          AverageTime: true,
          WaveLength: true,
          Ior: true,
          Pulse: true,
        };

        actionOtdr = {
          SamplingRate: true,
          MeasurementMode: true,
          FiberEndDetection: true,
          LossDetection: true,
          ReflectanceDetection: true,
          LaunchFiberStart: false,
          LaunchFiberEnd: false,
          Wavelength: true,
        };

        actionThresholds = []; // this.actionThresholdsList;
      } else if (scanType === ScanType.ConnectorCheck) {
        actionSettings = {
          Range: false,
          AverageTime: false,
          WaveLength: true,
          Ior: false,
          Pulse: false,
        };

        actionOtdr = {
          SamplingRate: false,
          MeasurementMode: false,
          FiberEndDetection: true,
          LossDetection: true,
          ReflectanceDetection: true,
          LaunchFiberStart: false,
          LaunchFiberEnd: false,
          Wavelength: true,
        };
        actionThresholds = [];
      } else {
        actionSettings = new ActionSettings();
        actionOtdr = new ActionOtdr();
        actionThresholds = [];
      }

      return {
        displayActionSettings: actionSettings.Pulse || actionSettings.Ior || actionSettings.Range || actionSettings.AverageTime || actionSettings.WaveLength,
        displayActionOtdr: actionOtdr.LaunchFiberEnd || actionOtdr.LaunchFiberStart || actionOtdr.FiberEndDetection || actionOtdr.LossDetection || actionOtdr.FiberEndDetection || actionOtdr.ReflectanceDetection || actionOtdr.MeasurementMode || actionOtdr.SamplingRate,
        displayActionThresholds: actionThresholds.length > 0,
        actionSettings,
        actionOtdr,
        actionThresholds,
      };
    },
  })),
  withStorageSync(`${storagePrefix}_data`, withSessionStorage()),
);
